import Vue from 'vue'
import * as types from '../mutation-types'
import judging from '@/api/judging'
import projects from '@/api/projects'
import { fetchStatuses } from '@/mixins/fetch-status'

const state = {
  currentTab: 'Pending',
  teams: {},
  teamsLoadingStatus: fetchStatuses.FetchStatusIdle,
  teamsLoadingError: null,
  teamsSwitchStatus: fetchStatuses.FetchStatusIdle,
  teamsSwitchError: null,
  teamLeadSetStatus: fetchStatuses.FetchStatusIdle,
  teamLeadSetError: null,
  teamUserRemoveStatus: fetchStatuses.FetchStatusIdle,
  teamUserRemoveError: null,
  teamGroups: {},
  teamGroupsStatus: fetchStatuses.FetchStatusIdle,
  teamGroupsError: null,
  teamLockStatus: fetchStatuses.FetchStatusIdle,
  teamLockError: null
}

const getters = {
  getCurrentTab: (state) => {
    return state.currentTab
  },
  getJudgingTeamGroups: (state) => {
    if (state.teamGroupsStatus === fetchStatuses.FetchStatusLoaded) {
      return Object.values(state.teamGroups)
    } else {
      return []
    }
  },
  getJudgingTeams: (state) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      return Object.values(state.teams)
    } else {
      return []
    }
  },
  getJudgingTeam: (state) => (teamId) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      return teamId in state.teams ? state.teams[teamId] : null
    }
  },
  getJudgingTeamStatus: (state) => {
    return state.teamsLoadingStatus
  },
  getJudgingTeamLockStatus: (state) => (teamId) => {
    if (state.teamsLoadingStatus === fetchStatuses.FetchStatusLoaded) {
      if (state.teams[teamId] && state.teams[teamId].projectStatus) {
        // Bitwise operator to make sure project status contains 16 (Locked)
        // e.g. status = 17 means 1 (pending) & 16 (locked)
        // eslint-disable-next-line no-self-compare
        return (state.teams[teamId].projectStatus.status & 16) === 16
      }
    }
    return false
  }
}

// actions
const actions = {
  switchTab ({ commit }, { tabName }) {
    commit(types.JUDGING_TAB_SWITCHED, tabName)
  },
  setJudgingTeamLockStatus ({ commit, getters }, { teamID }) {
    const currentLockStatus = getters.getJudgingTeamLockStatus(teamID)
    commit(types.JUDGING_SET_TEAM_LOCK_LOADING)
    if (!currentLockStatus) {
      projects.addProjectStatus(teamID, 'Locked').then((response) => {
        commit(types.JUDGING_SET_TEAM_LOCK_LOADED, { teamID: teamID, payload: response.data.result })
      }, (error) => {
        commit(types.JUDGING_SET_TEAM_LOCK_ERROR, error)
      })
    } else {
      projects.removeProjectStatus(teamID, 'Locked').then((response) => {
        commit(types.JUDGING_SET_TEAM_LOCK_LOADED, { teamID: teamID, payload: response.data.result })
      }, (error) => {
        commit(types.JUDGING_SET_TEAM_LOCK_ERROR, error)
      })
    }
  },
  fetchJudgingTeamGroups ({ commit }) {
    commit(types.JUDGING_TEAM_GROUPS_LOADING)
    judging.getJudgingTeamGroups().then((response) => {
      commit(types.JUDGING_TEAM_GROUPS_LOADED, response.data.result.groups)
    }, (error) => {
      commit(types.JUDGING_TEAM_GROUPS_ERROR, error)
    })
  },
  fetchJudgingTeam ({ commit }, { projectIDs }) {
    judging.getJudgingTeams(projectIDs).then((response) => {
      commit(types.JUDGING_TEAM_LOADED, response.data.result.teams)
    })
  },
  generateJudgingTeams ({ commit }, { teamSize, templateIds, teamTemplateId, filterOptions, diversifiedQuestionIds }) {
    commit(types.JUDGING_TEAM_LOADING)
    judging.generateJudgingTeams({ teamSize, templateIds, teamTemplateId, filterOptions, diversifiedQuestionIds }).then((response) => {
      commit(types.JUDGING_TEAM_LOADED, response.data.result.teams)
    }, (error) => {
      commit(types.JUDGING_TEAM_LOAD_ERROR, error)
    })
  },
  userSwitchTeam ({ commit }, { userName, fromTeam, toTeam }) {
    commit(types.JUDGING_TEAM_SWITCHING)
    judging.switchTeams({ userName, fromTeam, toTeam }).then((response) => {
      commit(types.JUDGING_TEAM_SWITCHED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_TEAM_SWITCH_ERROR, error)
    })
  },
  setTeamLead ({ commit }, { userName, teamID }) {
    commit(types.JUDGING_SET_TEAM_LEAD_PROCESSING)
    judging.setTeamLead({ userName, teamID }).then((response) => {
      commit(types.JUDGING_SET_TEAM_LEAD_PROCESSED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_SET_TEAM_LEAD_ERROR, error)
    })
  },
  removeTeamLead ({ commit }, { userName, teamID }) {
    commit(types.JUDGING_REMOVE_TEAM_LEAD_PROCESSING)
    judging.removeTeamLead({ userName, teamID }).then((response) => {
      commit(types.JUDGING_REMOVE_TEAM_LEAD_PROCESSED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_REMOVE_TEAM_LEAD_ERROR, error)
    })
  },
  removeFromTeam ({ commit }, { userName, fromTeam }) {
    commit(types.JUDGING_TEAM_USER_REMOVING)
    judging.removeUserFromTeam({ userName, teamID: fromTeam }).then((response) => {
      commit(types.JUDGING_TEAM_USER_REMOVED, response.data.result)
    }, (error) => {
      commit(types.JUDGING_TEAM_USER_REMOVE_ERROR, error)
    })
  }
}

const mutations = {
  [types.JUDGING_TAB_SWITCHED] (state, value) {
    state.currentTab = value
  },
  [types.JUDGING_TEAM_GROUPS_LOADING] (state) {
    state.teamGroupsStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_GROUPS_LOADED] (state, payload) {
    state.teamGroupsStatus = fetchStatuses.FetchStatusLoaded
    state.teamGroups = payload.reduce((result, current) => {
      result[current.id] = current
      return result
    }, {})
  },
  [types.JUDGING_TEAM_GROUPS_ERROR] (state) {
    state.teamGroupsStatus = fetchStatuses.FetchStatusError
  },
  [types.JUDGING_TEAM_LOADING] (state) {
    state.teamsLoadingStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_LOADED] (state, payload) {
    state.teams = payload.reduce((result, current) => {
      result[current.id] = current
      return result
    }, {})
    state.teamsLoadingStatus = fetchStatuses.FetchStatusLoaded
  },
  [types.JUDGING_TEAM_LOAD_ERROR] (state, error) {
    state.teamsLoadingError = error
    state.teamsLoadingStatus = fetchStatuses.FetchStatusError
  },
  [types.JUDGING_TEAM_SWITCHING] (state) {
    state.teamsSwitchStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_SWITCHED] (state, { fromTeam, toTeam }) {
    Vue.set(state.teams, fromTeam.id, fromTeam)
    Vue.set(state.teams, toTeam.id, toTeam)
    state.teamsSwitchStatus = fetchStatuses.FetchStatusLoaded
  },
  [types.JUDGING_TEAM_SWITCH_ERROR] (state, error) {
    state.teamsSwitchError = error
    state.teamsSwitchStatus = fetchStatuses.FetchStatusError
  },
  [types.JUDGING_SET_TEAM_LEAD_PROCESSING] (state) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_SET_TEAM_LEAD_PROCESSED] (state, payload) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_ERROR] (state, error) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusError
    state.teamLeadSetError = error
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_PROCESSING] (state) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_PROCESSED] (state, payload) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_REMOVE_TEAM_LEAD_ERROR] (state, error) {
    state.teamLeadSetStatus = fetchStatuses.FetchStatusError
    state.teamLeadSetError = error
  },
  [types.JUDGING_TEAM_USER_REMOVING] (state, error) {
    state.teamUserRemoveStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_TEAM_USER_REMOVED] (state, payload) {
    state.teamUserRemoveStatus = fetchStatuses.FetchStatusLoaded
    Vue.set(state.teams, payload.team.id, payload.team)
  },
  [types.JUDGING_TEAM_USER_REMOVE_ERROR] (state, error) {
    state.teamUserRemoveStatus = fetchStatuses.FetchStatusError
    state.teamUserRemoveError = error
  },
  [types.JUDGING_SET_TEAM_LOCK_LOADING] (state) {
    state.teamLockStatus = fetchStatuses.FetchStatusLoading
  },
  [types.JUDGING_SET_TEAM_LOCK_LOADED] (state, { teamID, payload }) {
    state.teamLockStatus = fetchStatuses.FetchStatusLoaded
    if (state.teams[teamID]) {
      const team = state.teams[teamID]
      if (team.projectStatus) {
        team.projectStatus.status = payload.status
      }
      Vue.set(state.teams, payload.projectID, team)
    }
  },
  [types.JUDGING_SET_TEAM_LOCK_ERROR] (state, error) {
    state.teamLockError = error
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
